import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const PrivacyPolicy = () => {
  const policySections = [
    {
      number: "01",
      title: "Information We Collect",
      content: "When you visit our site, we may collect the following types of information:",
      list: [
        "Personal Information: When you create an account, place an order, or subscribe to our newsletter, we collect personal information such as your name, email address, phone number, shipping address, and payment information.",
        "Non-Personal Information: We may collect non-personal information, such as your IP address, browser type, and access times, to help us improve our website and services."
      ]
    },
    {
      number: "02",
      title: "How We Use Your Information",
      content: "We use the information we collect for various purposes, including:",
      list: [
        "To process and fulfill your orders",
        "To communicate with you regarding your orders and inquiries",
        "To personalize your shopping experience",
        "To send promotional offers and updates (you can opt-out at any time)",
        "To improve our website and services"
      ]
    },
    {
      number: "03",
      title: "Sharing Your Information",
      content: "We do not sell or rent your personal information to third parties. However, we may share your information with trusted third-party service providers who assist us in operating our website, conducting our business, or servicing you, as long as those parties agree to keep your information confidential."
    },
{
      number: "04",
      title: "Data Security",
      content: "We implement a variety of security measures to maintain the safety of your personal information. Your sensitive information is encrypted and transmitted via Secure Socket Layer (SSL) technology. However, no method of transmission over the internet or method of electronic storage is 100% secure. Therefore, we cannot guarantee its absolute security."
    },
{
      number: "05",
      title: "Cookies",
      content: "Our website uses cookies to enhance your experience. Cookies are small files stored on your device that help us understand your preferences and improve our services. You can choose to accept or decline cookies through your browser settings. Please note that declining cookies may affect your ability to use certain features of our site."
    },
    {
      number: "06",
      title: "Third-Party Links",
      content: "Our website may contain links to third-party sites. We do not control these sites and are not responsible for their content or privacy practices. We encourage you to review their privacy policies before providing any personal information."
    },
    {
      number: "07",
      title: "Your Rights",
      content: "You have the right to:",
      list: [
        "Access the personal information we hold about you",
        "Request correction of any inaccuracies",
        "Request deletion of your personal information",
        "Opt-out of receiving marketing communications"
      ],
      note: "To exercise any of these rights, please contact us at hello@bakelore.com."
    },
    {
      number: "08",
      title: "Changes to This Privacy Policy",
      content: "We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on our website. Your continued use of the site after any changes constitutes your acceptance of the new policy."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Privacy Policy | Bakelore</title>
        <meta name="description" content="Bakelore Privacy Policy - Learn how we collect, use, and protect your personal information." />
        <meta name="keywords" content="Bakelore, Privacy Policy, Data Protection, Privacy" />
        <meta property="og:title" content="Privacy Policy | Bakelore" />
        <meta property="og:description" content="Learn how we collect, use, and protect your personal information." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Privacy Policy | Bakelore" />
        <meta name="twitter:description" content="Learn how we collect, use, and protect your personal information." />
      </Helmet>

      {/* Hero Banner */}
      <section className="relative h-[50vh] md:h-[60vh] overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=1920&q=80')",
          }}
        />
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#C08B89]/90 via-[#C08B89]/70 to-[#C08B89]/90" />
        
        {/* Decorative Elements */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-white/10 rounded-full blur-xl" />
        <div className="absolute bottom-20 right-10 w-48 h-48 bg-white/10 rounded-full blur-xl" />
        
        {/* Content */}
        <div className="relative z-10 h-full flex flex-col items-center justify-center px-6 text-center">
          <div className="inline-block mb-4 px-4 py-1 bg-white/20 rounded-full backdrop-blur-sm">
            <span className="text-white text-sm font-medium tracking-wider uppercase">
              Legal Information
            </span>
          </div>
          <h1 
            className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-4 tracking-tight"
            style={{ fontFamily: "Philosopher, serif" }}
          >
            Privacy Policy
          </h1>
          <p 
            className="text-white/90 text-lg md:text-xl max-w-2xl mb-8"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            Your privacy is important to us
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/contact">
              <button className="px-8 py-3 bg-white text-[#C08B89] font-semibold rounded-lg hover:bg-white/90 transition-all duration-300 shadow-lg">
                Contact Us
              </button>
            </Link>
            <Link to="/">
              <button className="px-8 py-3 border-2 border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-all duration-300">
                Back to Home
              </button>
            </Link>
          </div>
        </div>
        
        {/* Bottom Wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#faf8f5"/>
          </svg>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-12 md:py-16 bg-[#faf8f5]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 border border-gray-100">
            <div className="flex items-center justify-center mb-6">
              <div className="w-16 h-16 bg-[#C08B89]/10 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-[#C08B89]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
            </div>
            <p 
              className="text-gray-700 text-lg md:text-xl text-center leading-relaxed"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              At <span className="font-bold text-[#C08B89]">Bakelore</span>, we are committed to protecting your privacy. This Privacy Policy outlines how we collect, use, and safeguard your information when you visit our website or make a purchase from our online bakery store.
            </p>
          </div>
        </div>
      </section>

      {/* Policy Sections */}
      <section className="py-12 md:py-16 bg-[#faf8f5]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid gap-6">
            {policySections.map((section, index) => (
              <div 
                key={index}
                className="group bg-white rounded-xl p-6 md:p-8 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-[#C08B89]/30"
              >
                <div className="flex flex-col md:flex-row md:items-start gap-4">
                  <div className="flex-shrink-0">
                    <span className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-[#C08B89] to-[#d4a5a3] text-white font-bold text-lg rounded-lg">
                      {section.number}
                    </span>
                  </div>
                  <div className="flex-1">
                    <h2 
                      className="text-xl md:text-2xl font-bold text-gray-800 mb-3 group-hover:text-[#C08B89] transition-colors"
                      style={{ fontFamily: "Philosopher, serif" }}
                    >
                      {section.title}
                    </h2>
                    <p 
                      className="text-gray-600 leading-relaxed mb-4"
                      style={{ fontFamily: "Manrope, sans-serif" }}
                    >
                      {section.content}
                    </p>
                    {section.list && (
                      <ul className="space-y-2 mb-4">
                        {section.list.map((item, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-gray-600" style={{ fontFamily: "Manrope, sans-serif" }}>
                            <span className="text-[#C08B89] mt-1">•</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {section.note && (
                      <p className="text-gray-600 italic" style={{ fontFamily: "Manrope, sans-serif" }}>
                        {section.note}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-gradient-to-br from-[#C08B89] to-[#C08B89] rounded-2xl shadow-xl p-8 md:p-12 text-white">
            <div className="text-center mb-8">
              <h2 
                className="kint text-3xl md:text-4xl font-bold mb-4"
                style={{ fontFamily: "Philosopher, serif" }}
              >
                Have Questions?
              </h2>
              <p className="text-white/90 text-lg" style={{ fontFamily: "Manrope, sans-serif" }}>
                If you have any questions or concerns about this Privacy Policy, please contact us.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/20 transition-colors">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h3 className="kint kint font-semibold mb-2">Address</h3>
                <p className="text-white/80 text-sm">
                  1-103/5/1 Hitec City, Main Road, Madhapur, Hyderabad, Telangana, 500081.
                </p>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/20 transition-colors">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="kint font-semibold mb-2">Email</h3>
                <a href="mailto:hello@bakelore.com" className="text-white/80 hover:text-white hover:underline">
                  hello@bakelore.com
                </a>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/20 transition-colors">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <h3 className="kint font-semibold mb-2">Call/WhatsApp</h3>
                <div className="flex flex-col gap-1">
                  <a href="tel:+918121034991" className="text-white/80 hover:text-white hover:underline text-sm">+91 8121034991</a>
                  <a href="tel:+918121034992" className="text-white/80 hover:text-white hover:underline text-sm">+91 8121034992</a>
                  <a href="tel:+918121034993" className="text-white/80 hover:text-white hover:underline text-sm">+91 8121034993</a>
                </div>
              </div>
            </div>
            
            <div className="mt-8 text-center">
              <Link to="/contact">
                <button className="px-8 py-3 bg-white text-[#C08B89] font-semibold rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-lg">
                  Contact Us
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Thank You Section */}
      <section className="py-12 bg-[#faf8f5]">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <p 
            className="text-gray-700 text-xl"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            Thank you for choosing <span className="font-bold text-[#C08B89]">Bakelore</span>! We appreciate your trust in us.
          </p>
        </div>
      </section>
    </>
  );
};

export default PrivacyPolicy;

